import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  CheckCircle2,
  Sparkles,
  UserRound,
  Shirt,
  Eye,
  Footprints,
  HeartPulse,
  Zap,
  Flame,
  Snowflake,
  Scissors,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/skin-tag-removal-mumbai";
const MOLE_PATH = "/mole-removal-mumbai";
const CYST_PATH = "/sebaceous-cyst-removal-mumbai";
const KELOID_PATH = "/keloid-treatment-mumbai";

const SKIN_TAG_FAQS = [
  {
    question: "Is skin tag removal painful?",
    answer:
      "Most patients experience minimal discomfort. Topical numbing cream or a brief local anesthetic injection is applied before treatment in sensitive areas. The procedure itself takes a few minutes and most patients describe it as mild pressure or a brief sting rather than significant pain. Recovery is equally comfortable with only minor redness or tenderness for 1 to 2 days.",
  },
  {
    question: "Will there be a scar after skin tag removal?",
    answer:
      "In most cases, no visible scar remains. RF and electrocautery techniques remove the tag cleanly and leave a small healing area that resolves to normal skin within 1 to 2 weeks. Snip excision leaves a small, flat healing point that is similarly undetectable once healed. On cosmetically sensitive areas like the face and eyelids, Dr. Jain uses the technique most appropriate to minimize any mark. Cryotherapy occasionally causes temporary hypopigmentation, which resolves over weeks to months.",
  },
  {
    question: "Can I remove skin tags at home?",
    answer:
      "Home removal attempts are not recommended. Tying a thread around the stalk, cutting with scissors, or using over-the-counter removal products risks incomplete removal, excessive bleeding, infection, and scarring that is significantly worse than professional removal. Home methods also prevent clinical assessment that confirms the growth is a skin tag rather than another type of lesion. Professional removal is quick, safe, and produces a far better cosmetic result than home attempts.",
  },
  {
    question: "Can multiple skin tags be removed in one session?",
    answer:
      "Yes. Multiple tags in different locations can be removed in a single session. The total procedure time depends on the number of tags and the technique used but most multiple-tag sessions are completed within 30 to 45 minutes. Package pricing is available for patients with numerous tags.",
  },
  {
    question: "Will removing skin tags cause more to grow?",
    answer:
      "No. Removing a skin tag does not stimulate new ones to form. New tags that develop after removal form independently because the conditions that cause skin tags, friction, hormonal factors, and individual predisposition, continue to be present. This is not a result of the removal itself.",
  },
  {
    question: "How do I know if a growth is a skin tag or something else?",
    answer:
      "Skin tags are soft, smooth, flesh-colored, and hang from the skin on a narrow stalk. Warts are rough, firm, and viral in origin. Sebaceous cysts are firm, dome-shaped, and often have a central punctum. Raised moles are pigmented and sessile rather than hanging. Any growth that does not have the typical soft, hanging appearance of a skin tag, or that has changed in size or appearance, should be assessed clinically before removal to confirm the diagnosis.",
  },
  {
    question: "Is skin tag removal safe for diabetic patients?",
    answer:
      "Yes, with appropriate precautions. Diabetic patients heal more slowly than non-diabetic patients and are at higher risk of minor wound infection. Dr. Jain manages skin tag removal in diabetic patients with wound care instructions and follow-up appropriate to their condition. Diabetic patients should not attempt self-removal and should inform the treating doctor of their condition before any skin procedure.",
  },
  {
    question: "How long does the treated area take to heal?",
    answer:
      "For RF, electrocautery, and snip excision, the small healing area appears normal within 5 to 10 days. A small scab forms and falls off naturally during this period. For cryotherapy, the blister-like reaction resolves over 7 to 14 days. Sun protection of the treated area during healing reduces the risk of pigmentary changes, particularly on the face and neck.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Skin tag removal",
      description:
        "Minimally invasive removal of acrochordons using radiofrequency, electrocautery, cryotherapy, or snip excision in a clinic setting under plastic surgical care.",
      procedureType: "Minimally invasive",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Skin" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "MedicalCondition",
      name: "Acrochordon",
      alternateName: ["Skin tag"],
      description:
        "Benign pedunculated skin growth of collagen and epidermis; removed for comfort or cosmesis without malignant potential.",
      associatedAnatomy: { "@type": "AnatomicalStructure", name: "Skin" },
      possibleTreatment: [
        "Radiofrequency removal",
        "Electrocautery",
        "Cryotherapy",
        "Snip excision",
      ],
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: SKIN_TAG_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function SkinTagJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Skin Tag Removal", href: "/skin-tag-removal-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Reconstructive Surgery · Skin</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Skin Tag Removal</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Quick, Safe &amp; Scar-Minimized Skin Tag Treatment by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Skin Tag Removal" icon="🏷️" size="hero"  imageSrc={HERO.skinTagRemoval} imageAlt="Skin tag removal"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatAreSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-are-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What are Skin Tags?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A skin tag, medically called an acrochordon, is a small benign growth made of loose collagen and skin tissue that hangs from the skin surface on a narrow stalk. They are completely harmless, do not develop into cancer, and cause no medical concern. The reasons patients seek removal are cosmetic, the tags are visible or numerous, or practical, the tags catch on clothing or jewelry and cause repeated irritation or occasional bleeding.
            </p>
            <p>
              Skin tags are extremely common. Most adults develop at least a few during their lifetime, particularly in areas where skin folds create repeated friction. They increase in frequency with age, weight gain, hormonal changes, and in patients with diabetes or insulin resistance. Removing a skin tag does not cause more to form. New tags develop because the underlying condition, skin friction combined with individual predisposition, continues independent of whether existing tags are removed.
            </p>
            <p>
              Dr. Vicky Jain removes skin tags using radiofrequency, electrocautery, cryotherapy, or snip excision depending on the tag&apos;s size, location, and number. Most procedures take 5 to 15 minutes in the clinic with no hospital admission. Results are immediate. Most patients see smooth skin the same day.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Skin Tag Removal</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The first step in any skin tag assessment is confirming that the growth is indeed a skin tag and not another type of skin lesion. Soft fibromas, pedunculated moles, small warts, and other benign skin lesions can resemble skin tags clinically. The distinction matters because the appropriate removal technique, whether biopsy is needed, and the follow-up plan differ between these conditions. Dr. Jain examines each lesion before treatment to confirm the diagnosis.
              </p>
              <p>
                For straightforward skin tags, the technique is selected based on size, location, and the patient&apos;s preference for minimizing any visible mark after removal. Radiofrequency is the most commonly used technique because it combines precise removal with excellent control of bleeding and minimal tissue trauma. For eyelid skin tags, where proximity to the eye requires additional precision, and for very small tags, snip excision or cryotherapy may be preferred. Multiple tags can be treated in a single session for efficiency.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LocationsSection() {
  const cards = [
    {
      title: "Neck",
      icon: UserRound,
      copy: "The most common location. The neck collar area and nape are subject to repeated friction from clothing. Tags here are frequently visible and are the most common cosmetic reason patients seek removal.",
    },
    {
      title: "Underarms",
      icon: Shirt,
      copy: "The axilla is a friction-prone area where skin folds against itself during arm movement. Tags here are common and can catch on clothing or cause persistent irritation during exercise.",
    },
    {
      title: "Eyelids",
      icon: Eye,
      copy: "Small tags on the upper or lower eyelid margins can cause intermittent irritation of the eye. Eyelid tag removal requires particular precision due to proximity to the eye. Local anesthesia is used and the technique is adapted to the delicate anatomy of this location.",
    },
    {
      title: "Groin and Inner Thighs",
      icon: Footprints,
      copy: "High-friction areas in the groin folds and inner thigh skin are common sites for skin tag development, particularly in patients who are overweight. Tags in these locations are often numerous and can cause significant friction irritation.",
    },
    {
      title: "Under Breasts",
      icon: HeartPulse,
      copy: "The inframammary fold creates a chronic friction zone that promotes skin tag formation. Tags in this location are common in women and are frequently multiple.",
    },
    {
      title: "Face",
      icon: Sparkles,
      copy: "Small tags on the face, particularly around the eyes and along the jawline, are cosmetically prominent and are one of the more common reasons patients seek professional removal rather than attempting home treatment.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="locations-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Common Locations for Skin Tags</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_WART_ROWS = [
  {
    label: "Cause",
    tag: "Skin friction and individual predisposition. Not caused by a virus.",
    wart: "Human papillomavirus (HPV) infection.",
  },
  {
    label: "Texture",
    tag: "Soft, smooth, flesh-colored. Hangs on a stalk.",
    wart: "Rough, firm, cauliflower-like or flat surface. Disrupts normal skin lines.",
  },
  {
    label: "Pain",
    tag: "Painless unless traumatized.",
    wart: "Can be painful, particularly plantar warts on the sole of the foot.",
  },
  {
    label: "Contagious",
    tag: "No. Cannot spread to other people or to other areas by contact.",
    wart: "Yes. HPV can spread by direct contact to other skin areas or to other people.",
  },
  {
    label: "Treatment",
    tag: "Removal by RF, electrocautery, cryotherapy, or snip excision. Biopsy not typically required.",
    wart: "Requires antiviral or destructive treatment. Different technique from skin tag removal.",
  },
];

function SkinTagVsWartSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="skin-tag-vs-wart-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Skin Tags vs. Warts: How to Tell the Difference</h2>
        </div>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Skin Tag
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  Wart
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_WART_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.tag}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.wart}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          Correctly identifying a growth before treatment matters because warts require a different approach from skin tags and can spread if treated incorrectly. Any growth that appears rough, has an irregular surface, or is located where warts commonly develop, particularly on the hands, feet, and around the nails, should be assessed clinically before treatment is selected.
        </p>
      </div>
    </section>
  );
}

const REMOVAL_TECHNIQUES = [
  {
    title: "Radiofrequency (RF) Removal",
    icon: Zap,
    body:
      "The most commonly used technique. RF energy is applied to the base of the tag, cutting through the stalk and sealing the small blood vessels simultaneously. Produces minimal bleeding, a clean removal, and a small healing area that resolves to smooth skin. Most skin tags leave no visible mark after RF removal. The most appropriate technique for most skin tag sizes and locations. Takes a few minutes per tag.",
  },
  {
    title: "Electrocautery",
    icon: Flame,
    body:
      "Similar principle to RF. An electric current heats the instrument to cut through or burn off the tag. Effective and fast. Produces a small scab that falls off over 5 to 7 days to reveal normal skin. Used for small tags and as an alternative to RF in appropriate cases.",
  },
  {
    title: "Cryotherapy",
    icon: Snowflake,
    body:
      "Liquid nitrogen freezes the skin tag, causing cell death and gradual tag loss over 1 to 2 weeks as the frozen tissue sloughs off. Takes longer to show the final result than RF or snip excision. Useful for small tags and patients who prefer no instruments touching the tag during treatment. May cause temporary hypopigmentation at the treated site.",
  },
  {
    title: "Snip Excision",
    icon: Scissors,
    body:
      "The tag is grasped at its stalk and cut off cleanly with sterile scissors or a blade. Provides immediate removal. The small wound heals within a few days. Local anesthesia or topical numbing cream is applied before the procedure. The fastest technique for immediate removal and appropriate for most tag sizes.",
  },
];

function RemovalTechniquesSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="removal-techniques-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Skin Tag Removal Techniques</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {REMOVAL_TECHNIQUES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0" aria-hidden>
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
                  <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Skin Tag Removal is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The procedure takes 5 to 15 minutes in the clinic depending on the number of tags and technique used. No hospital admission is required. For most skin tag locations, topical numbing cream or a brief local anesthetic injection is applied before treatment. Tags in less sensitive areas such as the neck and body are sometimes removed without anesthesia as the procedure discomfort is minimal.
            </p>
            <p>
              The selected technique is applied to each tag. For RF and electrocautery, the tag is treated at its base and removed in a single application. For snip excision, the tag is grasped and cut. For cryotherapy, the probe is applied to the tag for a controlled freeze time. Multiple tags in the same session are treated systematically. A simple dressing or topical antibiotic ointment is applied at the end of the procedure.
            </p>
            <p>
              Patients leave the clinic immediately after treatment. Normal activity resumes the same day. The treated area heals over 3 to 7 days depending on the technique used. Most locations appear smooth and normal within one week.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Same Day",
      description:
        "The skin tag is removed. Immediate cosmetic improvement is visible. A small treated area remains at the removal site.",
      icon: Sparkles,
    },
    {
      period: "Days 2 to 3",
      description:
        "Minor redness and a small scab at the removal site for RF, electrocautery, and snip cases. Cryotherapy cases develop a blister-like reaction that resolves over 7 to 14 days.",
      icon: Clock,
    },
    {
      period: "Days 5 to 7",
      description:
        "Scabs fall off naturally. The skin underneath is pink and healing. Avoid scratching or picking the healing area.",
      icon: Monitor,
    },
    {
      period: "Week 1 to 2",
      description:
        "Skin appears normal at the removal site. Minimal or no visible mark in most cases. Sun protection of treated areas during this period reduces the risk of pigmentary changes.",
      icon: CheckCircle2,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ComeBackSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="come-back-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">What to Expect</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">Do Skin Tags Come Back After Removal?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The removed tag does not return. Once a skin tag is removed from its attachment point, it is gone permanently. What patients sometimes experience is the development of new skin tags in the same general area. This is not the same tag recurring. It is a new tag forming because the underlying conditions that create skin tags, friction, skin folding, and individual predisposition, continue after removal. The tendency to form skin tags is a systemic characteristic, not a local one.
            </p>
            <p>
              Patients who form new tags repeatedly after removal may benefit from addressing contributing factors: maintaining a stable weight reduces skin fold friction in the neck, underarm, and groin areas; controlling blood sugar in patients with diabetes or insulin resistance reduces the hormonal drive for tag formation; and wearing loose-fitting clothing in friction-prone areas reduces the mechanical stimulus. These measures do not prevent all new tag formation but reduce the frequency of new development.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoShouldConsiderSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-should-consider-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Who Should Consider Skin Tag Removal?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any adult with skin tags that cause cosmetic concern, repeated irritation from clothing or jewelry, occasional bleeding from trauma, or simply personal preference for their removal is an appropriate candidate. There is no clinical reason to keep a skin tag if removal is straightforward and the patient wants it removed. There is also no requirement to remove skin tags that cause no concern.
            </p>
            <p>
              Multiple skin tags can be treated in a single session for efficiency. Patients with a large number of tags across multiple areas may benefit from a staged approach, prioritizing the locations causing the most concern. Patients with diabetes should inform Dr. Jain during consultation as wound healing requires additional attention in diabetic patients even for minor procedures. Patients on blood thinning medication should also mention this as some techniques produce small amounts of bleeding that are relevant to manage in anticoagulated patients.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Lipoma" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {SKIN_TAG_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`skin-tag-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Skin Tag Removal Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain examines each tag to confirm the diagnosis and selects the appropriate removal technique for the location, size, and number of tags being treated. Multiple tags can be removed in the same session. You leave with smooth skin the same day in most cases.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Mole Removal",
      description: "Assessment and removal of pigmented or raised skin lesions alongside skin tag treatment.",
      href: MOLE_PATH,
    },
    {
      name: "Sebaceous Cyst Removal",
      description: "Removal of skin cysts that are sometimes confused with larger skin tags.",
      href: CYST_PATH,
    },
    {
      name: "Keloid Treatment",
      description: "Managing abnormal scar formation after skin tag removal in predisposed patients.",
      href: KELOID_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Related Procedures</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Related Procedures</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SkinTagRemovalPage() {
  return (
    <>
      <SEO
        title="Skin Tag Removal | Dr. Vicky Jain"
        description="Skin tags on neck, underarms, eyelids, or groin causing irritation or cosmetic concern. Dr. Vicky Jain removes skin tags quickly and safely. Book today."
        canonical="/skin-tag-removal-mumbai"
      />
      <SkinTagJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatAreSection />
      <DrJainApproachSection />
      <LocationsSection />
      <SkinTagVsWartSection />
      <RemovalTechniquesSection />
      <HowPerformedSection />
      <RecoveryTimelineSection />
      <ComeBackSection />
      <WhoShouldConsiderSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
